'use client'
import { FC, Fragment } from 'react'
import { DateTime } from 'luxon'
import cn from 'classnames'
import { Guest, ReservationItem } from '@/root/shared-types'
import { useAppConfig, useCustomRouter, useGetTranslations } from '@/root/shared-hooks'
import { CalendarIcon } from '@/root/ui/shared/icons/CalendarIcon'
import { ClockIcon } from '@/root/ui/shared/icons/ClockIcon'
import { ShipShortIcon } from '@/root/ui/shared/icons/ShipShortIcon'
import { PenIcon } from '@/icons/PenIcon'

interface RouteCardProps {
  routeName: string
  departurePort: string
  arrivalPort: string
  departure: string
  vesselTitle: string
  items: ReservationItem[]
  residents?: Guest[]
  columns: string[]
  onEdit?: () => void
}

export const RouteCard: FC<RouteCardProps> = ({
  departurePort,
  arrivalPort,
  departure,
  vesselTitle,
  items,
  residents,
  columns,
  onEdit,
}) => {
  const { formatMoney } = useAppConfig()
  const { locale } = useCustomRouter()
  const { getTranslation } = useGetTranslations()

  const formatDepartureDate = (isoDate: string) => {
    const dt = DateTime.fromISO(isoDate)
    const weekdayFull = dt.setLocale(locale).toFormat('ccc').toUpperCase()
    const weekday = locale === 'et' ? weekdayFull.slice(0, 1) : weekdayFull.slice(0, 2)
    return `${weekday} ${dt.toFormat('dd.MM.yyyy')}`
  }

  const departureDate = departure ? formatDepartureDate(departure) : ''
  const departureTime = departure ? DateTime.fromISO(departure).toFormat('HH:mm') : ''

  const itemsHeaderClass = 'text-neutral-normal-light text-xs lg:text-sm'

  return (
    <div className="flex w-full flex-col items-start justify-between gap-5 lg:flex-row lg:gap-8">
      {/* Route info */}
      <div className="flex shrink-0 flex-col items-start gap-y-5">
        <div className="text-primary-base text-lg leading-none font-medium">
          {departurePort} - {arrivalPort}
        </div>
        <div className="flex items-center gap-2.5">
          <CalendarIcon width={15} height={15} color="primary-base" />
          <span className="text-primary-base text-lg leading-none font-medium">{departureDate}</span>
        </div>
        <div className="flex items-center gap-2.5">
          <ClockIcon width={15} height={15} color="primary-base" />
          <span className="text-primary-base text-lg leading-none font-medium">{departureTime}</span>
        </div>
        <div className="flex items-center gap-2.5">
          <ShipShortIcon width={17} height={15} color="primary-base" />
          <span className="text-primary-base text-lg leading-none font-medium">{vesselTitle}</span>
        </div>
      </div>

      {/* Items table */}
      <div className="flex w-full flex-col items-start gap-2.5">
        {/* Edit button */}
        {onEdit && (
          <div className="flex w-full items-start justify-end pb-2.5">
            <button
              onClick={onEdit}
              className="border-neutral-dark flex h-7 items-center justify-center gap-2 rounded-lg border px-2.5"
            >
              <PenIcon width={16} height={16} color="neutral-medium-light" />
              <span className="text-neutral-medium-light text-sm leading-none font-medium">
                {getTranslation('customerInformationEdit')}
              </span>
            </button>
          </div>
        )}

        {/* Items table */}
        <div className="grid w-full grid-cols-[1fr_auto_auto_auto] items-start gap-x-4 gap-y-2.5 lg:gap-x-8">
          {/* Header row */}
          <div className={cn(itemsHeaderClass)}>{columns[0]}</div>
          <div className={cn(itemsHeaderClass, 'text-center')}>{columns[1]}</div>
          <div className={cn(itemsHeaderClass, 'text-center')}>{columns[2]}</div>
          <div className={cn(itemsHeaderClass, 'text-right')}>{columns[3]}</div>
          {/* Data rows */}
          {items.map((item, index) => (
            // oxlint-disable-next-line react/no-array-index-key
            <Fragment key={`${index}-${item.priceCategoryTranslation}`}>
              <div className="text-sm lg:text-base">{item.priceCategoryTranslation}</div>
              <div className="text-center text-sm lg:text-base">{item.quantity}</div>
              <div className="text-center text-sm lg:text-base">{formatMoney(item.pricePerUnit)}</div>
              <div className="text-right text-sm lg:text-base">{formatMoney(item.price)}</div>
            </Fragment>
          ))}
          {Boolean(residents?.length) && (
            <div className="text-primary-base">
              <p>{getTranslation('ticketLocalCustomers')}</p>
              <p>{residents?.map((r) => r.personalIdentificationNumber).join(', ')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
